import React, { useEffect, useMemo, useState } from 'react'
import { BarChart3, X } from 'lucide-react'
import type { GridController } from '../../compositor/GridController'
import type {
  SpreadsheetChartArtifactType,
  SpreadsheetDerivedArtifact,
  SpreadsheetRangeAddress,
  SpreadsheetRangeCell,
} from '../../protocol/spreadsheetEngineProtocol'
import {
  buildDerivedArtifactChartPreviewPresentation,
  buildDerivedArtifactPreviewModel,
  buildExcelRangeChartSettings,
  buildExcelRangeChartTitle,
  EXCEL_RANGE_CHART_TYPE_LABELS,
} from '../../spreadsheet/derivedArtifactPreview'
import {
  formatSparklineRangeFormula,
  parseSparklineRangeDraft,
} from '../../spreadsheet/sparklinePresentation'
import './ExcelWorkbookChrome.css'

type ExcelChartInsertDialogProps = {
  controller: GridController
  selectedRange: SpreadsheetRangeAddress
  selectionLabel: string
  initialType?: SpreadsheetChartArtifactType
  onClose: () => void
  onInserted?: () => void
}

const CHART_TYPES: SpreadsheetChartArtifactType[] = ['column', 'bar', 'line', 'area', 'pie', 'scatter']

const chartArtifactFromDraft = (
  sourceRange: SpreadsheetRangeAddress,
  chartType: SpreadsheetChartArtifactType,
): SpreadsheetDerivedArtifact => {
  const placement: SpreadsheetRangeAddress = {
    sheet_id: sourceRange.sheet_id,
    start_row: sourceRange.start_row,
    start_col: sourceRange.end_col + 1,
    end_row: sourceRange.start_row + 14,
    end_col: sourceRange.end_col + 6,
  }
  const uiRange = {
    startCol: sourceRange.start_col,
    startRow: sourceRange.start_row,
    endCol: sourceRange.end_col,
    endRow: sourceRange.end_row,
  }
  const settings = buildExcelRangeChartSettings({ range: uiRange, chartType })
  return {
    id: 'chart_insert_preview',
    sheet_id: sourceRange.sheet_id,
    kind: 'chart',
    source_range: sourceRange,
    placement,
    title: buildExcelRangeChartTitle(chartType),
    chart_type: chartType,
    metadata: settings ? { excel_range_chart: settings } : null,
  }
}

function renderPreviewSvg(args: {
  artifact: SpreadsheetDerivedArtifact
  cells: SpreadsheetRangeCell[][]
}) {
  const model = buildDerivedArtifactPreviewModel(args.artifact, args.cells)
  if (model.kind !== 'chart') return null
  const settings = buildExcelRangeChartSettings({ artifact: args.artifact })
  const presentation = buildDerivedArtifactChartPreviewPresentation(model, model.sourceAddress, settings)
  if (!presentation.hasRenderableContent || model.points.length === 0) {
    return <div className="fg-excel-chart-preview-empty">{presentation.emptyLabel}</div>
  }
  if (presentation.renderKind === 'pie') {
    return (
      <div className="fg-excel-chart-preview-pie-wrap">
        <div
          className="fg-excel-chart-preview-pie"
          style={{ background: presentation.pieGradient || 'conic-gradient(#107c41 0% 100%)' }}
          aria-hidden="true"
        />
        <div className="fg-excel-chart-preview-legend">
          {presentation.pieLegendItems.map((item) => (
            <span key={item.label}>
              <i style={{ background: item.color }} />
              {item.label}
            </span>
          ))}
        </div>
      </div>
    )
  }

  const points = model.plotPoints
  const plotPath = points
    .map((point) => `${point.xPct},${point.yPct}`)
    .join(' ')
  return (
    <svg className="fg-excel-chart-preview-svg" viewBox="0 0 100 100" role="img" aria-label={`${EXCEL_RANGE_CHART_TYPE_LABELS[model.chartType]} chart preview`}>
      <line x1="8" y1="88" x2="96" y2="88" className="fg-excel-chart-preview-axis" />
      <line x1="8" y1="8" x2="8" y2="88" className="fg-excel-chart-preview-axis" />
      {presentation.renderKind === 'bar' ? points.map((point, index) => {
        const width = Math.max(5, 72 / Math.max(1, points.length) - 2)
        const x = 14 + (index * (78 / Math.max(1, points.length)))
        const height = Math.max(2, 88 - point.yPct)
        return (
          <rect
            key={`${point.label}-${index}`}
            x={x}
            y={88 - height}
            width={width}
            height={height}
            rx="1.5"
            className="fg-excel-chart-preview-bar"
          />
        )
      }) : (
        <>
          {presentation.renderAreaFill && plotPath ? (
            <polygon
              points={`8,88 ${plotPath} 96,88`}
              className="fg-excel-chart-preview-area"
            />
          ) : null}
          <polyline
            points={plotPath}
            fill="none"
            className="fg-excel-chart-preview-line"
          />
          {points.map((point, index) => (
            <circle
              key={`${point.label}-${index}`}
              cx={point.xPct}
              cy={point.yPct}
              r={model.visualKind === 'scatter' ? 2.6 : 1.8}
              className="fg-excel-chart-preview-point"
            />
          ))}
        </>
      )}
    </svg>
  )
}

export function ExcelChartInsertDialog({
  controller,
  selectedRange,
  selectionLabel,
  initialType = 'column',
  onClose,
  onInserted,
}: ExcelChartInsertDialogProps) {
  const [chartType, setChartType] = useState<SpreadsheetChartArtifactType>(initialType)
  const [sourceRangeDraft, setSourceRangeDraft] = useState(() => formatSparklineRangeFormula(selectedRange, selectedRange.sheet_id))
  const [cells, setCells] = useState<SpreadsheetRangeCell[][] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const parsedSource = useMemo(
    () => parseSparklineRangeDraft(sourceRangeDraft, selectedRange.sheet_id),
    [selectedRange.sheet_id, sourceRangeDraft],
  )
  const previewArtifact = useMemo(
    () => parsedSource ? chartArtifactFromDraft(parsedSource.range, chartType) : null,
    [chartType, parsedSource],
  )

  useEffect(() => {
    if (!parsedSource) {
      setCells(null)
      setError('Enter a valid source range, such as A1:F12.')
      return undefined
    }
    const streamer = controller.streamer
    if (!streamer || typeof streamer.getRangeCells !== 'function') {
      setCells(null)
      setError('Chart preview is unavailable until the spreadsheet engine is ready.')
      return undefined
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    void streamer.getRangeCells(
      parsedSource.range.start_col,
      parsedSource.range.start_row,
      parsedSource.range.end_col,
      parsedSource.range.end_row,
      'computed',
      { sheetId: parsedSource.range.sheet_id },
    ).then((nextCells) => {
      if (cancelled) return
      setCells(nextCells)
      setError(null)
    }).catch((previewError) => {
      if (cancelled) return
      setCells(null)
      setError(previewError instanceof Error ? previewError.message : 'Chart preview could not be loaded.')
    }).finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [controller.streamer, parsedSource])

  const insertChart = () => {
    if (!parsedSource) {
      setError('Enter a valid source range before inserting a chart.')
      return
    }
    if (parsedSource.range.sheet_id !== selectedRange.sheet_id) {
      setError('Insert charts from the active sheet selection for now.')
      return
    }
    if (controller.insertChartArtifactFromRange(parsedSource.range, chartType)) {
      onInserted?.()
      onClose()
    }
  }

  return (
    <div className="fg-excel-format-dialog-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <div className="fg-excel-conditional-format-dialog fg-excel-chart-dialog" role="dialog" aria-modal="true" aria-label="Insert Chart">
        <div className="fg-excel-format-dialog-header">
          <strong className="fg-excel-cell-metadata-title">
            <BarChart3 size={16} />
            Insert Chart
          </strong>
          <button type="button" className="fg-excel-icon-button" aria-label="Close" title="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="fg-excel-cell-metadata-range">Selection {selectionLabel}</div>
        <div className="fg-excel-chart-dialog-body">
          <div className="fg-excel-format-grid fg-excel-chart-options-grid">
            <label>
              <span>Chart type</span>
              <select value={chartType} aria-label="Chart type" title="Chart type" onChange={(event) => setChartType(event.target.value as SpreadsheetChartArtifactType)}>
                {CHART_TYPES.map((type) => (
                  <option key={type} value={type}>{EXCEL_RANGE_CHART_TYPE_LABELS[type]}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Source range</span>
              <input value={sourceRangeDraft} aria-label="Chart source range" title="Chart source range" onChange={(event) => setSourceRangeDraft(event.target.value)} />
            </label>
          </div>
          <div className="fg-excel-chart-preview-panel">
            <div className="fg-excel-chart-preview-title">{buildExcelRangeChartTitle(chartType)}</div>
            {loading ? (
              <div className="fg-excel-chart-preview-empty">Loading preview...</div>
            ) : error ? (
              <div className="fg-excel-conditional-error">{error}</div>
            ) : previewArtifact && cells ? (
              renderPreviewSvg({ artifact: previewArtifact, cells })
            ) : (
              <div className="fg-excel-chart-preview-empty">Select a source range to preview.</div>
            )}
          </div>
        </div>
        <div className="fg-excel-format-footer">
          <button type="button" title="Cancel" onClick={onClose}>Cancel</button>
          <button type="button" title="Insert chart" className="fg-excel-cell-metadata-button-primary" onClick={insertChart}>Insert</button>
        </div>
      </div>
    </div>
  )
}
